// "Edit access" drawer (S7 team-scopes): toggle a member between org-wide and a
// limited set of groups/screens. Admin+ only — SettingsPage gates who can open it.

import { useState } from 'react';
import toast from 'react-hot-toast';
import { Drawer } from '../../components/Drawer.tsx';
import { Button } from '../../components/ui.tsx';
import { ApiError } from '../../api/client.ts';
import { useUpdateMemberScopes, type ScopedOrgMember } from '../../api/org.ts';
import { ScopePicker, useScopeNameMaps } from './ScopePicker.tsx';
import { scopeIdsByType, toScopeRefs, toOptimisticScopes } from './scopeUtils.ts';

export function MemberScopeDrawer({ member, onClose }: { member: ScopedOrgMember; onClose: () => void }) {
  const initial = scopeIdsByType(member.scopes);
  const [mode, setMode] = useState<'org-wide' | 'limited'>(member.scopes === null ? 'org-wide' : 'limited');
  const [screens, setScreens] = useState<Set<string>>(initial.screens);
  const [groups, setGroups] = useState<Set<string>>(initial.groups);
  const update = useUpdateMemberScopes();
  const { screenNames, groupNames } = useScopeNameMaps();

  const toggleScreen = (id: string) =>
    setScreens((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleGroup = (id: string) =>
    setGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  function save() {
    const scopes = mode === 'org-wide' ? null : toScopeRefs(screens, groups);
    const optimisticScopes =
      mode === 'org-wide' ? null : toOptimisticScopes(screens, groups, screenNames, groupNames);
    update.mutate(
      { userId: member.user_id, scopes, optimisticScopes },
      {
        onSuccess: () => {
          toast.success('Access updated');
          onClose();
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : 'Could not update access — try again'),
      },
    );
  }

  return (
    <Drawer title={`Edit access — ${member.name}`} onClose={onClose}>
      <div className="seg mb-4" role="radiogroup" aria-label="Access level">
        <button
          type="button"
          className={mode === 'org-wide' ? 'active' : ''}
          aria-pressed={mode === 'org-wide'}
          onClick={() => setMode('org-wide')}
        >
          Org-wide
        </button>
        <button
          type="button"
          className={mode === 'limited' ? 'active' : ''}
          aria-pressed={mode === 'limited'}
          onClick={() => setMode('limited')}
        >
          Limited
        </button>
      </div>

      {mode === 'limited' && (
        <>
          <div className="hint-box info mb-4">
            Limited members can only change these screens and their schedules. Media and playlists stay
            shared.
          </div>
          <ScopePicker
            selectedScreens={screens}
            selectedGroups={groups}
            onToggleScreen={toggleScreen}
            onToggleGroup={toggleGroup}
          />
        </>
      )}

      <div className="row mt-4">
        <Button variant="primary" onClick={save} disabled={update.isPending}>
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button onClick={onClose} disabled={update.isPending}>
          Cancel
        </Button>
      </div>
    </Drawer>
  );
}
